import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronRight,
  Pencil,
  Trash2,
  Calendar,
  Building2,
  Trophy,
  DollarSign,
  Plus,
  Target,
  Clock,
  Users,
  Lock,
  Shuffle,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { formatDateRange, formatCurrency } from '@/utils/formatters';
import { EditEventDialog, type EventFormData } from '@/components/delegate/EditEventDialog';
import {
  useUpdateJackpotDraft,
  jackpotOpsKeys,
} from '@/api/extensions/useJackpotOps';
import { httpClient } from '@/api/http-client';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/contexts/ToastContext';
import { getEventDisplayTitle } from '@/utils/eventTypes';

interface JackpotListRowProps {
  jackpot: any;
  orgName?: string;
  onEdit: (id: string) => void;
  onTogglePublish: (id: string) => void;
  onDelete: (id: string) => void;
}

function getHandicapDisplay(ev: any): string {
  const ht = ev.handicap_type;
  if (!ht || ht === 'none') return 'No Handicap Limits';

  if (ht === 'combined' && ev.max_combined_handicap != null) {
    return `Combined Max: ${ev.max_combined_handicap}`;
  }
  if (ht === 'individual' || ht === 'header_heeler' || ht === 'header-heeler') {
    const parts: string[] = [];
    if (ev.header_max_handicap != null) parts.push(`Header Max: ${ev.header_max_handicap}`);
    if (ev.heeler_max_handicap != null) parts.push(`Heeler Max: ${ev.heeler_max_handicap}`);
    return parts.length > 0 ? parts.join(', ') : String(ht).replace(/_/g, ' ');
  }

  return String(ht).replace(/_/g, ' ');
}

export function JackpotListRow({
  jackpot,
  orgName,
  onEdit,
  onTogglePublish,
  onDelete,
}: JackpotListRowProps) {
  const [expanded, setExpanded] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<{ data: EventFormData; index: number } | null>(null);
  const [addingEvent, setAddingEvent] = useState(false);
  const [regCounts, setRegCounts] = useState<Record<string, number>>({});
  const navigate = useNavigate();

  const isPublished = !!jackpot.is_published;
  const events = jackpot.events ?? [];
  const eventCount = events.length;

  // Fetch registration counts when expanded
  useEffect(() => {
    if (!expanded || eventCount === 0) return;
    const fetchCounts = async () => {
      const counts: Record<string, number> = {};
      await Promise.all(
        events.map(async (ev: any) => {
          try {
            const res = await httpClient.get<{ data: number; success: boolean }>(
              `/event-registrations/count?event_id=${ev.id}`
            );
            counts[ev.id] = (res as any)?.data ?? 0;
          } catch {
            counts[ev.id] = 0;
          }
        })
      );
      setRegCounts(counts);
    };
    fetchCounts();
  }, [expanded, eventCount]); // eslint-disable-line react-hooks/exhaustive-deps
  const totalPrize = events.reduce(
    (sum: number, ev: any) => sum + (Number(ev.total_prize_amount) || 0),
    0,
  );

  const updateDraft = useUpdateJackpotDraft();
  const queryClient = useQueryClient();
  const { showSuccess, showError, showWarning } = useToast();

  const handleEditEvent = useCallback((ev: any, index: number) => {
    if (isPublished) {
      showWarning('Jackpot is published', 'Unpublish the jackpot first to edit events.');
      return;
    }
    // Map API response rounds → dialog round_prizes format
    const roundPrizes: any[] | undefined =
      Array.isArray(ev.rounds) && ev.rounds.length > 0
        ? ev.rounds.map((r: any) => ({
            round_number: r.round_number,
            has_prizes: !!r.has_prizes || Number(r.round_prize_total) > 0,
            round_prize_total: Number(r.round_prize_total) || 0,
            is_progressive: !!r.is_progressive,
            places: Array.isArray(r.prizes)
              ? r.prizes.map((p: any) => ({
                  place: p.place,
                  percentage: Number(p.percentage) || 0,
                  prize_amount: Number(p.prize_amount) || 0,
                }))
              : [],
          }))
        : undefined;

    // Map API response adjustments → dialog prize_adjustments format
    const prizeAdjustments: any[] | undefined =
      Array.isArray(ev.adjustments) && ev.adjustments.length > 0
        ? ev.adjustments.map((a: any) => ({
            id: a.id || `adj-${Date.now()}-${Math.random()}`,
            operation: a.adjustment_type === 'addition' ? 'add' : 'subtract',
            adjustment_type: (Number(a.percentage) > 0 && Number(a.amount) === 0) ? 'percentage' : 'amount',
            amount: Number(a.amount) || 0,
            percentage: Number(a.percentage) || 0,
            reason: a.reason || '',
          }))
        : undefined;

    setEditingEvent({
      data: {
        id: ev.id,
        event_name: ev.event_name || '',
        entry_fee: ev.entry_fee,
        max_contestants: ev.max_contestants,
        handicap_type: ev.handicap_type,
        max_combined_handicap: ev.max_combined_handicap,
        header_max_handicap: ev.header_max_handicap,
        heeler_max_handicap: ev.heeler_max_handicap,
        max_individual_handicap: ev.max_individual_handicap,
        total_rounds: ev.total_rounds,
        is_century_jackpot: ev.is_century_jackpot,
        is_mixed_gender: ev.is_mixed_gender,
        total_prize_amount: ev.total_prize_amount,
        transaction_fee: ev.transaction_fee,
        slide_type: ev.slide_type,
        slide_base_handicap: ev.slide_base_handicap,
        slide_conversion_rate: ev.slide_conversion_rate,
        pick_count: ev.pick_count,
        draw_count: ev.draw_count,
        enforce_mutual_picks: ev.enforce_mutual_picks,
        ...(roundPrizes ? { round_prizes: roundPrizes } : {}),
        ...(prizeAdjustments ? { prize_adjustments: prizeAdjustments } : {}),
      },
      index,
    });
    setAddingEvent(false);
    setEditDialogOpen(true);
  }, [isPublished, showWarning]);

  const handleAddEvent = useCallback(() => {
    if (isPublished) {
      showWarning('Jackpot is published', 'Unpublish the jackpot first to add events.');
      return;
    }
    setEditingEvent(null);
    setAddingEvent(true);
    setEditDialogOpen(true);
  }, [isPublished, showWarning]);

  /** Extract only the fields the API EventInputSchema accepts */
  const toApiEvent = useCallback((ev: any) => {
    const trimmedName = typeof ev.event_name === 'string' ? ev.event_name.trim() : '';
    const out: Record<string, unknown> = {
      // event_name is optional — send null when blank so the API/UI falls
      // back to the event type as the display title.
      event_name: trimmedName ? trimmedName : null,
    };
    if (ev.id) out.id = ev.id;
    // Optional numeric fields (min 0)
    for (const k of [
      'entry_fee', 'transaction_fee', 'total_prize_amount',
      'header_max_handicap', 'heeler_max_handicap',
      'max_combined_handicap', 'max_individual_handicap',
      'slide_conversion_rate', 'slide_base_handicap',
    ] as const) {
      if (ev[k] != null) out[k] = Number(ev[k]);
    }
    // Optional integer fields — total_rounds has minimum:1, skip if 0.
    // max_contestants treats 0 as "Unlimited" (= unset on the events
    // row), so we drop non-positive values here too. Surfacing 0 to the
    // events row would let the registration capacity check read it back
    // as "always full" and block every signup.
    for (const k of [
      'total_rounds', 'short_round_size', 'short_round_count', 'max_contestants',
    ] as const) {
      if (ev[k] != null) {
        const val = Number(ev[k]);
        if (k === 'total_rounds' && val < 1) continue;
        if (k === 'max_contestants' && val <= 0) continue;
        out[k] = val;
      }
    }
    // Optional string fields
    for (const k of ['handicap_type', 'event_type_id', 'slide_type', 'registration_deadline'] as const) {
      if (ev[k] != null) out[k] = ev[k];
    }
    // Optional boolean fields
    for (const k of ['elimination_enabled', 'is_century_jackpot', 'is_mixed_gender'] as const) {
      if (ev[k] != null) out[k] = ev[k];
    }
    // Rounds — handle both formats:
    //   1. EditEventDialog sends `round_prizes` (with `places` sub-array)
    //   2. API response has `rounds` (with `prizes` sub-array containing `place`/`percentage`)
    const rawRounds: any[] | undefined =
      (ev.round_prizes && Array.isArray(ev.round_prizes) && ev.round_prizes.length > 0)
        ? ev.round_prizes
        : (ev.rounds && Array.isArray(ev.rounds) && ev.rounds.length > 0)
          ? ev.rounds
          : undefined;
    if (rawRounds) {
      out.rounds = rawRounds.map((rp: any) => {
        const roundNum = rp.round_number != null ? Number(rp.round_number) : 1;
        const prizesArr = rp.places ?? rp.prizes;
        const validPrizes = Array.isArray(prizesArr) && prizesArr.length > 0
          ? prizesArr.map((p: any) => ({
              place: Math.max(1, Number(p.place) || 1),
              percentage: Number(p.percentage) || 0,
            }))
          : undefined;
        const result: Record<string, unknown> = {
          round_number: roundNum,
          is_progressive: rp.is_progressive ?? false,
        };
        if (rp.has_prizes != null) result.has_prizes = !!rp.has_prizes;
        if (rp.round_prize_total != null) result.round_prize_total = Number(rp.round_prize_total) || 0;
        if (rp.contestants_advancing != null) result.contestants_advancing = Number(rp.contestants_advancing);
        if (validPrizes) result.prizes = validPrizes;
        return result;
      });
    }
    // Prize adjustments — handle both formats
    const rawAdj: any[] | undefined =
      (ev.prize_adjustments && Array.isArray(ev.prize_adjustments) && ev.prize_adjustments.length > 0)
        ? ev.prize_adjustments
        : (ev.adjustments && Array.isArray(ev.adjustments) && ev.adjustments.length > 0)
          ? ev.adjustments
          : undefined;
    if (rawAdj) {
      const mapped = rawAdj
        .filter((a: any) => !a.markedForDeletion)
        .map((a: any) => {
          let adjType: 'addition' | 'subtraction';
          if (a.operation === 'add' || a.operation === 'subtract') {
            adjType = a.operation === 'add' ? 'addition' : 'subtraction';
          } else if (a.adjustment_type === 'addition' || a.adjustment_type === 'subtraction') {
            adjType = a.adjustment_type;
          } else {
            adjType = 'subtraction';
          }
          // DB CHECK: (amount > 0 AND percentage IS NULL) OR (amount = 0 AND percentage IS NOT NULL)
          // EditEventDialog: adjustment_type='amount'|'percentage' means calculation method
          // API/DB: adjustment_type='addition'|'subtraction' means direction
          const isPercentageBased =
            (a.adjustment_type === 'percentage') ||
            (Number(a.percentage) > 0 && Number(a.amount) === 0);
          const result: Record<string, unknown> = {
            adjustment_type: adjType,
            reason: a.reason || 'Adjustment',
            ...(a.description ? { description: a.description } : {}),
            ...(a.is_active != null ? { is_active: a.is_active } : {}),
          };
          if (isPercentageBased) {
            // percentage-based: amount must be 0, percentage must be set
            result.amount = 0;
            result.percentage = Number(a.percentage) || Number(a.amount) || 0;
          } else {
            // dollar-based: amount > 0, do NOT send percentage (must be NULL in DB)
            result.amount = Number(a.amount) || 0;
            // Explicitly omit percentage so DB gets NULL
          }
          return result;
        });
      if (mapped.length > 0) out.adjustments = mapped;
    }
    return out;
  }, []);

  const handleEventSave = useCallback(async (data: EventFormData) => {
    // Guard: cannot save on published jackpots
    if (isPublished) {
      showError('Cannot edit published jackpot', 'Unpublish the jackpot first, then edit events.');
      setEditDialogOpen(false);
      return;
    }

    try {
      // Build updated events array — only send API-safe fields
      const currentEvents = (jackpot.events ?? []).map((ev: any) => toApiEvent(ev));

      let updatedEvents: any[];
      if (addingEvent) {
        updatedEvents = [...currentEvents, toApiEvent(data)];
      } else if (editingEvent) {
        updatedEvents = currentEvents.map((ev: any, i: number) => {
          if (i !== editingEvent.index) return ev;
          const merged = { ...ev, ...toApiEvent(data) };
          if (!data.id && ev.id) merged.id = ev.id;
          return merged;
        });
      } else {
        return;
      }

      const payload: Record<string, unknown> = {
        jackpotId: jackpot.id,
        name: jackpot.name,
        events: updatedEvents,
      };
      const clean = JSON.parse(JSON.stringify(payload));
      await updateDraft.mutateAsync(clean as any);
      queryClient.invalidateQueries({ queryKey: jackpotOpsKeys.lists() });
      showSuccess('Event saved');
      setEditDialogOpen(false);
    } catch (err) {
      // Extract server error message if available
      const msg = err instanceof Error ? err.message : 'Unknown error';
      if (msg.includes('published')) {
        showError('Cannot edit published jackpot', 'Unpublish the jackpot first, then edit events.');
      } else {
        showError('Save failed', msg);
      }
    }
  }, [jackpot, isPublished, editingEvent, addingEvent, toApiEvent, updateDraft, queryClient, showSuccess, showError]);

  return (
    <div className="rounded-lg bg-card text-card-foreground overflow-hidden shadow-sm border border-border">
      {/* Header row */}
      <div
        className="flex items-center gap-3 px-4 py-3 cursor-pointer select-none bg-muted/80 hover:bg-muted transition-colors"
        onClick={() => setExpanded((prev) => !prev)}
      >
        <ChevronRight
          className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 ${
            expanded ? 'rotate-90' : ''
          }`}
        />

        {/* Name + badges + info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-semibold text-sm truncate">{jackpot.name}</h3>
            <Badge
              variant={isPublished ? 'default' : 'secondary'}
              className={`text-xs shrink-0 ${
                isPublished
                  ? 'bg-green-600 hover:bg-green-700 text-white'
                  : 'bg-yellow-500 hover:bg-yellow-600 text-white'
              }`}
            >
              {isPublished ? 'Published' : 'Draft'}
            </Badge>
            {/* Money amount — always visible */}
            <span className="inline-flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400 font-medium shrink-0">
              <DollarSign className="h-3 w-3" />
              {formatCurrency(totalPrize)}
            </span>
          </div>

          {/* Second line: date range + org + event count */}
          <div className="flex items-center gap-3 mt-0.5 text-xs text-muted-foreground flex-wrap">
            {(jackpot.start_date || jackpot.end_date) && (
              <span className="flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                {formatDateRange(jackpot.start_date, jackpot.end_date)}
              </span>
            )}
            {orgName && (
              <span className="flex items-center gap-1">
                <Building2 className="h-3 w-3" />
                {orgName}
              </span>
            )}
            <span className="flex items-center gap-1">
              <Trophy className="h-3 w-3" />
              {eventCount} event{eventCount !== 1 ? 's' : ''}
            </span>
          </div>
        </div>

        {/* Action buttons */}
        <div
          className="flex items-center gap-1.5 shrink-0"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Prominent status toggle button */}
          <Button
            size="sm"
            className={`text-xs h-7 px-2.5 ${
              isPublished
                ? 'bg-yellow-600 hover:bg-yellow-700 text-white'
                : 'bg-green-600 hover:bg-green-700 text-white'
            }`}
            onClick={() => onTogglePublish(jackpot.id)}
          >
            {isPublished ? 'Set Inactive' : 'Set Active'}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            title="Edit"
            onClick={() => onEdit(jackpot.id)}
          >
            <Pencil className="h-4 w-4" />
          </Button>
          {!isPublished && (
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-destructive hover:text-destructive"
              title="Delete"
              onClick={() => onDelete(jackpot.id)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Expanded details */}
      {expanded && (
        <div className="border-t px-4 py-4 space-y-4">
          {/* Info grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <div className="space-y-2">
              <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Details
              </h4>
              {(jackpot.start_date || jackpot.end_date) && (
                <p className="flex items-center gap-2 text-muted-foreground">
                  <Calendar className="h-4 w-4 shrink-0 text-blue-500" />
                  {formatDateRange(jackpot.start_date, jackpot.end_date)}
                </p>
              )}
              {orgName && (
                <p className="flex items-center gap-2 text-muted-foreground">
                  <Building2 className="h-4 w-4 shrink-0 text-orange-500" />
                  {orgName}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Prize Information
              </h4>
              <p className="flex items-center gap-2 text-muted-foreground">
                <DollarSign className="h-4 w-4 shrink-0 text-emerald-500" />
                Total: {formatCurrency(totalPrize)}
              </p>
              <p className="flex items-center gap-2 text-muted-foreground">
                <Trophy className="h-4 w-4 shrink-0 text-amber-500" />
                {eventCount} event{eventCount !== 1 ? 's' : ''}
              </p>
            </div>
          </div>

          {jackpot.description && (
            <p className="text-sm text-muted-foreground">{jackpot.description}</p>
          )}

          {/* Published notice */}
          {isPublished && (
            <div className="flex items-center gap-2 text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30 rounded-md px-3 py-2">
              <Lock className="h-3.5 w-3.5 shrink-0" />
              Events are locked while published. Set Inactive to edit events.
            </div>
          )}

          {/* Events section */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Events
              </h4>
              {!isPublished && (
                <Button
                  size="sm"
                  className="h-7 text-xs bg-blue-600 hover:bg-blue-700 text-white"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAddEvent();
                  }}
                >
                  <Plus className="h-3.5 w-3.5 mr-1" />
                  Add Event
                </Button>
              )}
            </div>

            {eventCount > 0 ? (
              <div className="space-y-2">
                {events.map((ev: any, index: number) => (
                  <EventRow
                    key={ev.id}
                    event={ev}
                    showEditButton={!isPublished}
                    onEditEvent={() => handleEditEvent(ev, index)}
                    registrationCount={regCounts[ev.id] ?? null}
                    onManageDraw={() => navigate('/delegate/draws', { state: { eventId: ev.id, eventName: getEventDisplayTitle(ev), jackpotId: jackpot.id } })}
                  />
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground italic">
                No events configured yet.{' '}
                {!isPublished && 'Click "Add Event" to create one.'}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Inline Edit Event Dialog */}
      <EditEventDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        event={editingEvent?.data ?? null}
        onSave={handleEventSave}
      />
    </div>
  );
}

/* ─── Individual Event Row (inside expanded jackpot) ─── */

function EventRow({
  event: ev,
  showEditButton,
  onEditEvent,
  registrationCount,
  onManageDraw,
}: {
  event: any;
  showEditButton: boolean;
  onEditEvent: () => void;
  registrationCount: number | null;
  onManageDraw: () => void;
}) {
  const rounds = ev.rounds ?? [];
  const totalRounds = Number(ev.total_rounds) || rounds.length;
  const prizeAmount = Number(ev.total_prize_amount) || 0;
  const handicapText = getHandicapDisplay(ev);

  // Build round pills — use actual rounds data if available, otherwise generate from total_rounds
  const roundPills: Array<{
    key: string;
    roundNumber: number;
    isProgressive: boolean;
    hasPrizes: boolean;
    prizeTotal: number;
  }> = [];

  if (rounds.length > 0) {
    for (const round of [...rounds].sort((a: any, b: any) => (a.round_number ?? 0) - (b.round_number ?? 0))) {
      roundPills.push({
        key: round.id ?? `r-${round.round_number}`,
        roundNumber: round.round_number,
        isProgressive: !!round.is_progressive,
        hasPrizes: !!round.has_prizes || Number(round.round_prize_total) > 0,
        prizeTotal: Number(round.round_prize_total) || 0,
      });
    }
  } else if (totalRounds > 0) {
    for (let i = 1; i <= totalRounds; i++) {
      roundPills.push({
        key: `gen-${i}`,
        roundNumber: i,
        isProgressive: false,
        hasPrizes: false,
        prizeTotal: 0,
      });
    }
  }

  return (
    <div className="rounded-lg border-2 border-border bg-muted/30 p-3 hover:border-blue-400 hover:bg-blue-50/50 dark:hover:bg-blue-950/20 transition-colors group">
      {/* Event header */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-sm">{getEventDisplayTitle(ev)}</span>
            {/* Prize amount — always show */}
            <span className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">
              {formatCurrency(prizeAmount)}
            </span>
          </div>
        </div>

        {/* Event actions */}
        <div className="flex items-center gap-2 shrink-0">
          {registrationCount != null && (
            <span className="text-xs text-blue-500 font-medium flex items-center gap-1">
              <Users className="h-3 w-3" />
              {registrationCount} registered
            </span>
          )}
          <Button
            variant="outline"
            size="sm"
            className="h-6 text-xs px-2"
            title="Manage Draw"
            onClick={(e) => {
              e.stopPropagation();
              onManageDraw();
            }}
          >
            <Shuffle className="h-3 w-3 mr-1" />
            Draw
          </Button>
          {showEditButton && (
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
              title="Edit Event"
              onClick={(e) => {
                e.stopPropagation();
                onEditEvent();
              }}
            >
              <Pencil className="h-3 w-3" />
            </Button>
          )}
        </div>
      </div>

      {/* Event details grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2 text-xs text-muted-foreground">
        {/* Handicap info */}
        <span className="flex items-center gap-1">
          <Target className="h-3 w-3 text-purple-500 shrink-0" />
          <span className="truncate">{handicapText}</span>
        </span>

        {/* Max contestants */}
        {ev.max_contestants && (
          <span className="flex items-center gap-1">
            <Users className="h-3 w-3 text-orange-500 shrink-0" />
            Max: {ev.max_contestants}
          </span>
        )}

        {/* Rounds count */}
        {totalRounds > 0 && (
          <span className="flex items-center gap-1">
            <Clock className="h-3 w-3 text-blue-500 shrink-0" />
            {totalRounds} Round{totalRounds !== 1 ? 's' : ''}
          </span>
        )}

        {/* Entry fee */}
        {ev.entry_fee != null && Number(ev.entry_fee) > 0 && (
          <span className="flex items-center gap-1">
            <DollarSign className="h-3 w-3 text-emerald-500 shrink-0" />
            Entry: {formatCurrency(ev.entry_fee)}
          </span>
        )}
      </div>

      {/* Round-by-round breakdown pills */}
      {roundPills.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2 pt-2 border-t border-border/50">
          {roundPills.map((rp) => (
            <span
              key={rp.key}
              className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                rp.isProgressive
                  ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300'
                  : rp.hasPrizes
                    ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
                    : 'bg-muted text-muted-foreground'
              }`}
            >
              Round {rp.roundNumber}
              {rp.isProgressive && ' (Progressive)'}
              {rp.hasPrizes && rp.prizeTotal > 0 && ` ${formatCurrency(rp.prizeTotal)}`}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
